import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, ArrowLeft, Loader2, Lock, Wand2, ImageIcon, Download,
  Trash2, RefreshCw, ChevronDown, ChevronUp, Copy, UploadCloud, X,
  FolderOpen, FlaskConical,
} from "lucide-react";
import ProjectFolderPicker from "../components/ProjectFolderPicker";
import TypographyPicker from "../components/TypographyPicker";
import CategoryPicker from "../components/CategoryPicker";
import { useAuth } from "../context/AuthContext";

/**
 * AIStudioTool — shared page used by every AI Studio generator.
 * Each tool page passes its config (title, icon, endpoint, form fields).
 * Handles the full flow: gate on add-on, render form, run generation,
 * show images + brief, list past generations, allow re-download / delete.
 *
 * When `supportsReferences` is true the endpoint accepts multipart form
 * data (`payload` JSON + up to 3 `references` image files).
 */
export default function AIStudioTool({
  toolSlug,          // "packaging" | "logo" | …
  name,              // "Packaging Designer"
  Icon,              // lucide icon component
  tagline,           // one-liner under the title
  fields,            // ordered list of field descriptors
  endpoint,          // "/ai-studio/packaging/generate"
  defaultValues,     // initial form values
  cta = "Generate concepts",
  historyLabel = "Recent generations",
  textOnly = false,  // set to true for tools that don't render images
  footerNote,        // custom caption above the Generate button
  formHeading = "Tell us about the brand",
  supportsReferences = false,   // enable the reference-image uploader
  supportsFeedback = false,     // enable "Regenerate with feedback" on results
  demoInputs = null,            // admin-only auto-fill presets: {label, values}[]
}) {
  const { user } = useAuth();
  const nav = useNavigate();
  const canAutofill = ["super_admin", "admin"].includes((user?.role || "").toLowerCase());
  const [status, setStatus] = useState(null);          // ai-addon status
  // Plan-gating check: null = loading, true = allowed, false = blocked.
  const [planAllowed, setPlanAllowed] = useState(null);
  const [form, setForm] = useState(defaultValues || {});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);          // current generation
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [references, setReferences] = useState([]);    // File[]
  const fileInputRef = useRef(null);
  // Post-generation "Save to folder?" modal. `moveTarget` = the just-created
  // generation whose auto-saved project row can still be moved into a folder.
  const [moveTarget, setMoveTarget] = useState(null);

  const active = !!status?.active;
  const monthlyImages = status?.used?.images || 0;
  const monthlyImagesCap = status?.allowance?.images || 0;
  const monthlyChats = status?.used?.chats || 0;
  const monthlyChatsCap = status?.allowance?.chats || 0;

  useEffect(() => {
    api.get("/ai-addon/status").then(r => setStatus(r.data))
      .catch(() => setStatus({ active: false }));
  }, []);

  // Enforce the same plan gating the sidebar and /ai-center grid apply.
  // Short toolSlug (e.g. "packaging") → menu id ("packaging-designer") →
  // check menu-visibility.children_allowed["nav-ai-center"].
  const TOOL_TO_MENU_ID = {
    "packaging":    "packaging-designer",
    "logo":         "logo-designer",
    "meta-ads":     "meta-ads-manager",
    "reddit-ads":   "reddit-ads-manager",
    "ads-designer": "ads-designer",
    "formulation":  "formulation-creator",
  };
  useEffect(() => {
    const menuId = TOOL_TO_MENU_ID[toolSlug] || toolSlug;
    api.get("/menu-visibility").then((r) => {
      const forbidden = new Set(((r.data?.children_allowed || {})["nav-ai-center"]) || []);
      const overrides = (r.data?.children_overrides || {})["nav-ai-center"] || {};
      if (overrides[menuId]?.disabled) forbidden.add(menuId);
      setPlanAllowed(!forbidden.has(menuId));
    }).catch(() => {
      // Fail closed — if we can't confirm access, block the UI so a
      // network error can't bypass admin-configured plan gating.
      setPlanAllowed(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolSlug]);

  const loadHistory = () => {
    setHistoryLoading(true);
    api.get(`/ai-studio/generations?tool=${toolSlug}&limit=12`)
      .then(r => setHistory(r.data.items || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };
  useEffect(() => {
    // Formulation Creator is free for every paid member, so we load its
    // history regardless of AI Studio add-on status. Image-generating
    // tools only have history to show once the user has activated the
    // add-on, so we keep the `active` gate for them.
    if (active || toolSlug === "formulation") loadHistory();
    /* eslint-disable-line */
  }, [active, toolSlug]);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const r = await api.post("/ai-addon/checkout", { origin_url: window.location.origin });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatError(e)); setSubscribing(false); }
  };

  const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const addReferences = (files) => {
    const list = Array.from(files || []);
    const filtered = list.filter(f => f.type && f.type.startsWith("image/"));
    if (filtered.length !== list.length) toast.error("Only image files can be used as references.");
    const available = Math.max(0, 3 - references.length);
    if (filtered.length > available) {
      toast.error("Maximum 3 reference images.");
    }
    setReferences(prev => [...prev, ...filtered].slice(0, 3));
  };
  const removeReference = (idx) => {
    setReferences(prev => prev.filter((_, i) => i !== idx));
  };

  const submit = async (override) => {
    // `override` is an optional payload used by the regenerate-with-feedback
    // flow — it replaces `form` entirely so we can send the parent gen's
    // inputs plus the new feedback fields without disturbing what the founder
    // has typed in the form.
    //
    // Defensive guard: if a caller accidentally binds this to a DOM handler
    // (`onClick={submit}`), the React SyntheticEvent gets passed as `override`
    // and JSON.stringify would blow up on the circular structure — leading to
    // a silent "nothing happens" click. Treat any non-plain-object as "no
    // override" and fall back to the form state.
    const isEventLike = override && typeof override === "object" &&
      ("nativeEvent" in override || typeof override.preventDefault === "function");
    if (isEventLike) override = undefined;
    const payload = override || form;
    if (!override) {
      for (const f of fields) {
        if (f.required && !String(form[f.key] || "").trim()) {
          toast.error(`${f.label} is required`);
          return;
        }
      }
    }
    setRunning(true);
    setResult(null);
    // Snapshot start-time so we can recover if the ingress cuts the
    // connection (Cloudflare ~60s cap on the preview URL) mid-call.
    const submittedAt = new Date().toISOString();
    try {
      let r;
      if (supportsReferences) {
        // Always multipart for image tools — even without references — so
        // one code path serves both cases.
        const fd = new FormData();
        fd.append("payload", JSON.stringify(payload));
        references.forEach(file => fd.append("references", file, file.name));
        r = await api.post(endpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,   // image edit calls can push toward 90-120s
        });
      } else {
        r = await api.post(endpoint, payload, { timeout: 180000 });
      }
      setResult(r.data);
      api.get("/ai-addon/status").then(res => setStatus(res.data)).catch(() => {});
      loadHistory();
      const cnt = r.data.image_count || 0;
      toast.success(
        cnt > 0
          ? `${name} — ${cnt} concept${cnt === 1 ? "" : "s"} ready · saved to Projects`
          : `${name} — brief ready · saved to Projects`,
      );
      if (r.data.project_id) {
        setMoveTarget({
          project_id: r.data.project_id,
          project_name: r.data.project_name || name,
        });
      }
    } catch (e) {
      // Preview URL / Cloudflare kills long requests at ~60s but the
      // backend still finishes and auto-saves the record. Poll once for a
      // freshly-created generation before surfacing the error.
      const status = e?.response?.status;
      const wasNetworkOrTimeout = !status || status === 502 || status === 504 || status === 408;
      if (wasNetworkOrTimeout) {
        try {
          await new Promise(res => setTimeout(res, 3000));
          const hr = await api.get(`/ai-studio/generations?tool=${toolSlug}&limit=1`);
          const latest = hr.data?.items?.[0];
          if (latest && latest.created_at && latest.created_at > submittedAt) {
            setResult(latest);
            api.get("/ai-addon/status").then(res => setStatus(res.data)).catch(() => {});
            loadHistory();
            toast.success(`${name} — recovered from timeout · saved to Projects`);
            if (latest.project_id) {
              setMoveTarget({
                project_id: latest.project_id,
                project_name: latest.project_name || name,
              });
            }
            return;
          }
        } catch { /* fall through to error */ }
      }
      toast.error(formatError(e));
    } finally { setRunning(false); }
  };

  const remove = async (genId) => {
    if (!window.confirm("Delete this generation? Images will be permanently removed.")) return;
    try {
      await api.delete(`/ai-studio/generations/${genId}`);
      setHistory(h => h.filter(x => x.id !== genId));
      if (result?.id === genId) setResult(null);
    } catch (e) { toast.error(formatError(e)); }
  };

  /**
   * Regenerate an existing gen with a founder-supplied feedback note.
   * Reuses the gen's original inputs (so brand name, category, typography,
   * etc. all carry over) and appends {feedback, regenerate_from} for the
   * backend prompt-builder to weave in.
   */
  const regenerate = async (gen, feedback) => {
    if (!supportsFeedback) return;
    const trimmed = (feedback || "").trim();
    if (!trimmed) { toast.error("Type what you'd like changed first."); return; }
    // Merge the historical inputs with the fresh feedback. Drop any prior
    // feedback so the model isn't confused by two overlapping notes.
    const payload = {
      ...(gen.inputs || {}),
      feedback: trimmed,
      regenerate_from: gen.id,
    };
    await submit(payload);
  };

  const copyPrompt = async (prompt) => {
    try { await navigator.clipboard.writeText(prompt); toast.success("Prompt copied"); }
    catch { toast.error("Could not copy"); }
  };

  const busy = status === null || planAllowed === null;
  const cardsSection = useMemo(() => (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields
        .filter(f => (typeof f.visibleWhen === "function" ? f.visibleWhen(form) : true))
        .map(f => (
          <FormField
            key={f.key}
            field={f}
            form={form}
            value={form[f.key] ?? ""}
            onChange={(v) => setField(f.key, v)}
          />
        ))}
    </div>
  ), [fields, form]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppShell title={name}>
      <div className="w-full">
        <Link
          to="/ai-cpg-tools/brand-profile"
          data-testid="ai-tool-back"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to AI CPG Tools
        </Link>

        <PageHero
          eyebrow="AI Studio · Live tool"
          icon={Icon}
          title={name}
          subtitle={tagline}
        />

        {busy ? (
          <div className="mt-8 surface p-6 flex items-center gap-3 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your access…
          </div>
        ) : planAllowed === false ? (
          <div className="mt-8 surface p-6" data-testid="ai-tool-plan-blocked">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              <p className="font-display text-xl">{name} isn't included on your plan</p>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Upgrade your CPG Institute plan to unlock this AI Studio tool. Every plan comes with a
              specific set of generative tools — see the pricing page for the full breakdown.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Link
                to="/account/subscription"
                data-testid="ai-tool-plan-upgrade"
                className="btn-primary text-sm inline-flex items-center gap-1.5"
              >
                See upgrade options
              </Link>
              <button
                onClick={() => nav("/ai-cpg-tools/brand-profile")}
                data-testid="ai-tool-plan-back"
                className="btn-secondary text-sm"
              >
                Back to AI CPG Tools
              </button>
            </div>
          </div>
        ) : !active && toolSlug !== "formulation" ? (
          <div className="mt-8 surface p-6" data-testid="ai-tool-locked">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              <p className="font-display text-xl">Subscribe to the AI Studio add-on</p>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              This tool is included in the $25/mo AI Studio bundle. One subscription unlocks
              every generative tool the moment it ships.
            </p>
            <button
              onClick={subscribe}
              disabled={subscribing}
              data-testid="ai-tool-subscribe"
              className="mt-5 btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {subscribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Subscribe · $25/mo
            </button>
          </div>
        ) : (
          <>
            {/* Meters — hidden for Formulation Creator since it's free
                for every paid plan with no monthly cap. */}
            {toolSlug !== "formulation" && (
              <div className="mt-8 surface p-5" data-testid="ai-studio-tool-meter">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <p className="font-display text-lg">This month's allowance</p>
                  <p className="text-xs text-muted-foreground">
                    Overage is metered and billed on the 1st.
                  </p>
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-4">
                  {!textOnly && (
                    <MeterBar label="Image generations" used={monthlyImages} cap={monthlyImagesCap} testid="ai-tool-meter-images" />
                  )}
                  <MeterBar label="AI briefs (chats)" used={monthlyChats} cap={monthlyChatsCap} testid="ai-tool-meter-chats" />
                </div>
              </div>
            )}

            {/* Form */}
            <div className="mt-6 surface p-6" data-testid="ai-tool-form">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    Brief the AI
                  </p>
                  <h2 className="font-display text-2xl mt-1">{formHeading}</h2>
                </div>
                {canAutofill && Array.isArray(demoInputs) && demoInputs.length > 0 && (
                  <AutofillPicker
                    presets={demoInputs}
                    onPick={(preset) => {
                      setForm({ ...(defaultValues || {}), ...(preset.values || {}) });
                      toast.success(`Loaded demo inputs: ${preset.label}`);
                    }}
                  />
                )}
              </div>
              <div className="mt-5">{cardsSection}</div>

              {supportsReferences && (
                <ReferenceUploader
                  files={references}
                  onAdd={addReferences}
                  onRemove={removeReference}
                  fileInputRef={fileInputRef}
                />
              )}

              <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-muted-foreground max-w-xl">
                  {footerNote || (textOnly
                    ? "Briefs typically finish in 15–25 seconds."
                    : "Concepts render in about 30–60 seconds. This tool renders images — a human designer should still refine the winner before print.")}
                </p>
                <button
                  onClick={() => submit()}
                  disabled={running}
                  data-testid="ai-tool-generate"
                  className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                  {running ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-3.5 h-3.5" /> {cta}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Live result */}
            {result && (
              <ResultCard
                gen={result}
                onRemove={() => remove(result.id)}
                onCopyPrompt={() => copyPrompt(result.prompt)}
                onRegenerate={supportsFeedback ? (fb) => regenerate(result, fb) : null}
                regenerating={running}
                testid="ai-tool-current-result"
              />
            )}

            {/* History */}
            <div className="mt-10">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h2 className="font-display text-2xl">{historyLabel}</h2>
                <button
                  onClick={loadHistory}
                  data-testid="ai-tool-refresh-history"
                  className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>

              {historyLoading ? (
                <div className="mt-4 text-sm text-muted-foreground inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : history.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">
                  No generations yet — your first concepts will appear here.
                </p>
              ) : (
                <div className="mt-4 grid gap-4 grid-cols-1 min-w-0" data-testid="ai-tool-history">
                  {history.map(gen => (
                    <HistoryRow
                      key={gen.id}
                      gen={gen}
                      expanded={expandedId === gen.id}
                      onToggle={() => setExpandedId(id => id === gen.id ? null : gen.id)}
                      onRemove={() => remove(gen.id)}
                      onCopyPrompt={() => copyPrompt(gen.prompt)}
                      onRegenerate={supportsFeedback ? (fb) => regenerate(gen, fb) : null}
                      regenerating={running}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {moveTarget && (
        <SaveToFolderModal
          target={moveTarget}
          onClose={() => setMoveTarget(null)}
        />
      )}
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function FormField({ field, form, value, onChange }) {
  const testid = `ai-tool-field-${field.key}`;
  if (field.type === "typography") {
    return (
      <label className={"block text-sm " + (field.wide ? "sm:col-span-2" : "")}>
        <span className="font-semibold">{field.label}{field.required && <span className="text-primary"> *</span>}</span>
        {field.hint && <span className="block text-xs text-muted-foreground mt-0.5">{field.hint}</span>}
        <div className="mt-1.5">
          <TypographyPicker
            value={value}
            onChange={onChange}
            brandNamePreview={field.previewFrom ? form[field.previewFrom] : ""}
            testid={testid}
          />
        </div>
      </label>
    );
  }
  if (field.type === "category") {
    return (
      <label className={"block text-sm " + (field.wide ? "sm:col-span-2" : "")}>
        <span className="font-semibold">{field.label}{field.required && <span className="text-primary"> *</span>}</span>
        {field.hint && <span className="block text-xs text-muted-foreground mt-0.5">{field.hint}</span>}
        <div className="mt-1.5">
          <CategoryPicker value={value} onChange={onChange} testid={testid} />
        </div>
      </label>
    );
  }
  if (field.type === "select") {
    const opts = typeof field.optionsFor === "function"
      ? field.optionsFor(form || {})
      : field.options || [];
    return (
      <label className="block text-sm">
        <span className="font-semibold">{field.label}{field.required && <span className="text-primary"> *</span>}</span>
        {field.hint && <span className="block text-xs text-muted-foreground mt-0.5">{field.hint}</span>}
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          data-testid={testid}
          className="mt-1.5 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
        >
          {opts.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </label>
    );
  }
  if (field.type === "number") {
    return (
      <label className="block text-sm">
        <span className="font-semibold">{field.label}{field.required && <span className="text-primary"> *</span>}</span>
        {field.hint && <span className="block text-xs text-muted-foreground mt-0.5">{field.hint}</span>}
        <input
          type="number"
          value={value}
          min={field.min ?? 1}
          max={field.max ?? 4}
          onChange={e => onChange(Number(e.target.value))}
          data-testid={testid}
          className="mt-1.5 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
      </label>
    );
  }
  if (field.type === "textarea") {
    return (
      <label className={"block text-sm " + (field.wide ? "sm:col-span-2" : "")}>
        <span className="font-semibold">{field.label}{field.required && <span className="text-primary"> *</span>}</span>
        {field.hint && <span className="block text-xs text-muted-foreground mt-0.5">{field.hint}</span>}
        <textarea
          value={value}
          rows={field.rows || 3}
          maxLength={field.maxLength || 600}
          placeholder={field.placeholder}
          onChange={e => onChange(e.target.value)}
          data-testid={testid}
          className="mt-1.5 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
      </label>
    );
  }
  return (
    <label className={"block text-sm " + (field.wide ? "sm:col-span-2" : "")}>
      <span className="font-semibold">{field.label}{field.required && <span className="text-primary"> *</span>}</span>
      {field.hint && <span className="block text-xs text-muted-foreground mt-0.5">{field.hint}</span>}
      <input
        type="text"
        value={value}
        maxLength={field.maxLength || 200}
        placeholder={field.placeholder}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
      />
    </label>
  );
}

function MeterBar({ label, used, cap, testid }) {
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const over = cap && used > cap;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-semibold">{label}</span>
        <span className={over ? "text-primary font-semibold" : "text-muted-foreground"}>
          {used} / {cap}
        </span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
        <div className={"h-full rounded-full " + (over ? "bg-primary" : "bg-shell-side")}
             style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// AutofillPicker — admin-only "load a curated sample brief" dropdown that
// slots into the top-right of the form. Single preset renders as one button;
// multiple presets render as a `Fill: …` dropdown so admins can flip between
// QA scenarios (premium beverage, wellness supplement, retro snack, …).
function AutofillPicker({ presets, onPick }) {
  const [open, setOpen] = useState(false);
  const only = presets.length === 1 ? presets[0] : null;

  if (only) {
    return (
      <button
        type="button"
        onClick={() => onPick(only)}
        data-testid="ai-tool-autofill-single"
        title="Admin only — populate the form with a curated sample brief for testing."
        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-pink-500/60 bg-pink-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-pink-700 hover:bg-pink-100 transition"
      >
        <FlaskConical className="w-3.5 h-3.5" />
        Fill demo inputs
      </button>
    );
  }
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="ai-tool-autofill-menu"
        title="Admin only — populate the form with a curated sample brief for testing."
        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-pink-500/60 bg-pink-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-pink-700 hover:bg-pink-100 transition"
      >
        <FlaskConical className="w-3.5 h-3.5" />
        Fill demo inputs
        <ChevronDown className={["w-3 h-3 transition-transform", open ? "rotate-180" : ""].join(" ")} />
      </button>
      {open && (
        <div
          className="absolute z-40 right-0 top-full mt-1 min-w-[16rem] rounded-lg border border-shell-border bg-white shadow-lg p-1"
          onMouseLeave={() => setOpen(false)}
          data-testid="ai-tool-autofill-list"
        >
          {presets.map((p, i) => (
            <button
              key={i}
              onClick={() => { onPick(p); setOpen(false); }}
              data-testid={`ai-tool-autofill-${i}`}
              className="block w-full text-left px-3 py-2 rounded hover:bg-muted transition"
            >
              <span className="block text-sm font-semibold">{p.label}</span>
              {p.hint && (
                <span className="block text-[10.5px] text-muted-foreground mt-0.5">{p.hint}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function ReferenceUploader({ files, onAdd, onRemove, fileInputRef }) {
  const [dragging, setDragging] = useState(false);
  const previews = useMemo(
    () => files.map(f => ({ name: f.name, size: f.size, url: URL.createObjectURL(f) })),
    [files],
  );
  useEffect(() => {
    return () => previews.forEach(p => URL.revokeObjectURL(p.url));
  }, [previews]);

  const remaining = 3 - files.length;

  return (
    <div className="mt-6 rounded-xl border border-shell-border bg-muted/30 p-4"
         data-testid="ai-tool-reference-uploader">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">
            Reference images (optional)
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Drop up to 3 images to show the AI the aesthetic you're chasing —
            competitor packs, Pinterest saves, mood-board shots. PNG/JPG/WEBP, max 6 MB each.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {files.length} / 3
        </span>
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault();
          setDragging(false);
          if (remaining <= 0) return;
          onAdd(e.dataTransfer.files);
        }}
        onClick={() => remaining > 0 && fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        data-testid="ai-tool-reference-dropzone"
        className={
          "mt-3 rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition " +
          (dragging
            ? "border-primary bg-primary/5"
            : "border-shell-border hover:border-primary/50 hover:bg-white")
        }
      >
        <UploadCloud className="w-6 h-6 mx-auto text-muted-foreground" />
        <p className="mt-2 text-sm font-semibold">
          {remaining > 0
            ? "Drag images here, or click to browse"
            : "Maximum of 3 references attached"}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          The AI will use these to guide composition, colour palette, and finish — not copy them exactly.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid="ai-tool-reference-input"
          onChange={e => {
            onAdd(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {previews.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="ai-tool-reference-list">
          {previews.map((p, idx) => (
            <div
              key={idx}
              data-testid={`ai-tool-reference-${idx}`}
              className="relative rounded-lg border border-shell-border overflow-hidden bg-white group"
            >
              <img src={p.url} alt={p.name} className="block w-full h-32 object-cover" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
                data-testid={`ai-tool-reference-remove-${idx}`}
                className="absolute top-1.5 right-1.5 w-6 h-6 grid place-items-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-black transition"
                aria-label={`Remove ${p.name}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <p className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 text-[10px] text-white truncate">
                {p.name}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultCard({ gen, onRemove, onCopyPrompt, onRegenerate, regenerating, testid }) {
  const hasImages = (gen.image_count || 0) > 0;
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");

  const submitFeedback = () => {
    if (!onRegenerate) return;
    onRegenerate(feedback);
    setFeedback("");
    setShowFeedback(false);
  };

  return (
    <div className="mt-8 surface p-6" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-primary font-semibold">
            {gen.inputs?.regenerate_from ? "Iteration" : "Just generated"}
          </p>
          <p className="font-display text-2xl mt-0.5">
            {hasImages
              ? `${gen.image_count} concept${gen.image_count === 1 ? "" : "s"}`
              : "AI brief ready"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onRegenerate && (
            <button
              onClick={() => setShowFeedback((v) => !v)}
              disabled={regenerating}
              data-testid={`${testid}-regenerate-toggle`}
              className="text-xs font-semibold border border-primary/40 text-primary rounded-full px-3 py-1.5 hover:bg-primary/5 inline-flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={"w-3 h-3 " + (regenerating ? "animate-spin" : "")} />
              {showFeedback ? "Hide feedback" : "Regenerate with feedback"}
            </button>
          )}
          {hasImages && (
            <button
              onClick={onCopyPrompt}
              data-testid="ai-tool-result-copy-prompt"
              className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted inline-flex items-center gap-1"
            >
              <Copy className="w-3 h-3" /> Copy prompt
            </button>
          )}
          <button
            onClick={onRemove}
            data-testid="ai-tool-result-delete"
            className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted text-red-600 inline-flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </div>

      {showFeedback && onRegenerate && (
        <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4"
             data-testid={`${testid}-feedback-panel`}>
          <p className="text-[10px] uppercase tracking-widest text-primary font-semibold">
            What do you want changed?
          </p>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            placeholder="e.g. Keep the wordmark but try a warmer palette. The 'W' should feel more architectural. Drop the tagline."
            data-testid={`${testid}-feedback-input`}
            className="mt-2 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            We'll keep your brief and re-render with your notes applied. Your first render stays in the history.
          </p>
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => { setShowFeedback(false); setFeedback(""); }}
              disabled={regenerating}
              data-testid={`${testid}-feedback-cancel`}
              className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={submitFeedback}
              disabled={regenerating || !feedback.trim()}
              data-testid={`${testid}-feedback-submit`}
              className="btn-primary text-xs disabled:opacity-60"
            >
              {regenerating ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Regenerating…</>
              ) : (
                <><RefreshCw className="w-3 h-3" /> Regenerate</>
              )}
            </button>
          </div>
        </div>
      )}

      {hasImages && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {(gen.image_urls || []).map((url, idx) => (
            <ImageTile key={idx} url={url} label={`Concept ${idx + 1}`} testid={`ai-tool-result-image-${idx}`} />
          ))}
        </div>
      )}

      <BriefPanel brief={gen.brief} primary={!hasImages} />
    </div>
  );
}

function ImageTile({ url, label, testid }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const src = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  return (
    <div className="relative rounded-xl overflow-hidden border border-shell-border bg-muted"
         data-testid={testid}>
      {!loaded && !failed && (
        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-xs">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )}
      {!failed ? (
        <img
          src={src}
          alt={label}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={"block w-full h-auto transition-opacity " + (loaded ? "opacity-100" : "opacity-0")}
        />
      ) : (
        <div className="p-6 text-xs text-muted-foreground">
          <ImageIcon className="w-4 h-4 inline-block mr-1" />
          Preview unavailable
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-3 flex items-center justify-between gap-2 bg-gradient-to-t from-black/70 to-transparent">
        <span className="text-white text-xs font-semibold uppercase tracking-widest">{label}</span>
        <a
          href={src}
          download
          data-testid={testid + "-download"}
          className="text-white text-xs font-semibold inline-flex items-center gap-1 bg-white/10 rounded-full px-3 py-1.5 hover:bg-white/20 backdrop-blur"
        >
          <Download className="w-3 h-3" /> PNG
        </a>
      </div>
    </div>
  );
}

function BriefPanel({ brief, primary = false }) {
  if (!brief || Object.keys(brief).length === 0) return null;
  const entries = Object.entries(brief).filter(
    ([k]) => k !== "image_prompt" && k !== "raw",
  );
  if (!entries.length) return null;
  return (
    <div
      className={
        "rounded-xl border p-5 min-w-0 overflow-hidden " +
        (primary
          ? "mt-2 border-shell-border bg-white"
          : "mt-6 border-shell-border bg-muted/60")
      }
      data-testid="ai-tool-brief-panel"
    >
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        {primary ? "AI-generated brief" : "AI design brief"}
      </p>
      <div className={"mt-3 grid gap-4 [&>*]:min-w-0 " + (primary ? "" : "sm:grid-cols-2")}>
        {entries.map(([k, v]) => (
          <BriefField key={k} keyName={k} value={v} primary={primary} />
        ))}
      </div>
    </div>
  );
}

function BriefField({ keyName, value, primary }) {
  const label = keyName.replace(/_/g, " ");
  const isPrimitive = typeof value === "string" || typeof value === "number" || typeof value === "boolean";
  const isArrayOfPrimitives =
    Array.isArray(value) &&
    value.every(v => typeof v === "string" || typeof v === "number");
  const isArrayOfObjects =
    Array.isArray(value) && value.length > 0 && value.every(v => v && typeof v === "object");
  const isPlainObject =
    value && typeof value === "object" && !Array.isArray(value);

  // Shared wrap style — snake_case values (`functional_energy_drink`,
  // `zero_sugar, natural_caffeine, clear`), long URLs, and ingredient
  // lists have no natural whitespace break points, so without this
  // every cell on the AI Design Brief overflows the card to the
  // right at mobile + iPad + narrow desktop. `overflow-wrap: anywhere`
  // breaks mid-token when nothing else will fit (user feedback
  // 2026-10-11).
  const wrap = "min-w-0 break-words [overflow-wrap:anywhere]";

  return (
    <div className={"text-sm " + wrap}>
      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      {isPrimitive && <p className={"mt-1 " + wrap}>{String(value)}</p>}
      {isArrayOfPrimitives && (
        <ul className="mt-1 flex flex-wrap gap-1.5">
          {value.map((item, i) => (
            <li key={i} className={"rounded-full bg-muted px-2.5 py-1 text-xs " + wrap}>
              {String(item)}
            </li>
          ))}
        </ul>
      )}
      {isArrayOfObjects && (
        <ul className={"mt-1 space-y-2 " + (primary ? "" : "sm:col-span-2")}>
          {value.map((row, i) => (
            <li key={i} className={"rounded-lg border border-shell-border bg-white/80 p-3 text-xs " + wrap}>
              {Object.entries(row).map(([rk, rv]) => (
                <div key={rk} className="flex flex-wrap gap-1.5 mb-1 last:mb-0">
                  <span className="font-semibold uppercase tracking-widest text-[10px] text-muted-foreground shrink-0">
                    {rk.replace(/_/g, " ")}:
                  </span>
                  <span className={wrap}>
                    {Array.isArray(rv) ? rv.join(", ") : String(rv)}
                  </span>
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
      {isPlainObject && (
        <div className={"mt-1 rounded-lg border border-shell-border bg-white/80 p-3 text-xs space-y-1.5 " + wrap}>
          {Object.entries(value).map(([sk, sv]) => (
            <div key={sk} className="flex flex-wrap gap-1.5">
              <span className="font-semibold uppercase tracking-widest text-[10px] text-muted-foreground shrink-0">
                {sk.replace(/_/g, " ")}:
              </span>
              <span className={wrap}>
                {Array.isArray(sv) ? sv.join(", ") : String(sv)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ gen, expanded, onToggle, onRemove, onCopyPrompt, onRegenerate, regenerating }) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");

  const submitFeedback = () => {
    if (!onRegenerate) return;
    onRegenerate(feedback);
    setFeedback("");
    setShowFeedback(false);
  };

  return (
    <div className="surface p-4 min-w-0 overflow-hidden" data-testid={`ai-tool-history-row-${gen.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex -space-x-2">
            {(gen.image_urls || []).slice(0, 3).map((url, idx) => (
              <img
                key={idx}
                src={url.startsWith("http") ? url : `${BACKEND_URL}${url}`}
                alt={`Concept ${idx + 1}`}
                className="w-11 h-11 rounded-lg object-cover border border-white shadow-sm"
              />
            ))}
          </div>
          <div className="min-w-0">
            <p className="font-semibold truncate">
              {(gen.inputs && (gen.inputs.product_name || gen.inputs.brand_name)) || "Untitled"}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {gen.summary || gen.prompt?.slice(0, 120)}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
              {new Date(gen.created_at).toLocaleString()} · {gen.image_count} image{gen.image_count === 1 ? "" : "s"}
              {gen.inputs?.regenerate_from && <> · <span className="text-primary">iteration</span></>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onToggle}
            data-testid={`ai-tool-history-toggle-${gen.id}`}
            className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted inline-flex items-center gap-1"
          >
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {expanded ? "Hide" : "View"}
          </button>
          {onRegenerate && (
            <button
              onClick={() => setShowFeedback((v) => !v)}
              disabled={regenerating}
              data-testid={`ai-tool-history-regenerate-${gen.id}`}
              className="text-xs font-semibold border border-primary/40 text-primary rounded-full px-3 py-1.5 hover:bg-primary/5 inline-flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={"w-3 h-3 " + (regenerating ? "animate-spin" : "")} />
              Iterate
            </button>
          )}
          <button
            onClick={onCopyPrompt}
            data-testid={`ai-tool-history-copy-${gen.id}`}
            className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted inline-flex items-center gap-1"
          >
            <Copy className="w-3 h-3" /> Prompt
          </button>
          <button
            onClick={onRemove}
            data-testid={`ai-tool-history-delete-${gen.id}`}
            className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted text-red-600 inline-flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </div>
      {showFeedback && onRegenerate && (
        <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4"
             data-testid={`ai-tool-history-feedback-panel-${gen.id}`}>
          <p className="text-[10px] uppercase tracking-widest text-primary font-semibold">
            What do you want changed in the next iteration?
          </p>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            placeholder="e.g. Keep the wordmark but try a warmer palette. Drop the tagline."
            data-testid={`ai-tool-history-feedback-input-${gen.id}`}
            className="mt-2 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          />
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => { setShowFeedback(false); setFeedback(""); }}
              disabled={regenerating}
              className="text-xs font-semibold border border-shell-border rounded-full px-3 py-1.5 hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={submitFeedback}
              disabled={regenerating || !feedback.trim()}
              data-testid={`ai-tool-history-feedback-submit-${gen.id}`}
              className="btn-primary text-xs disabled:opacity-60"
            >
              {regenerating ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Regenerating…</>
              ) : (
                <><RefreshCw className="w-3 h-3" /> Regenerate</>
              )}
            </button>
          </div>
        </div>
      )}
      {expanded && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {(gen.image_urls || []).map((url, idx) => (
            <ImageTile key={idx} url={url} label={`Concept ${idx + 1}`} testid={`ai-tool-history-image-${gen.id}-${idx}`} />
          ))}
        </div>
      )}
      {expanded && <BriefPanel brief={gen.brief} />}
    </div>
  );
}


// ---------------------------------------------------------------------------
// SaveToFolderModal — shown once per generation. The generation is already
// auto-saved to Projects at root (see backend `ai_studio._save_generation`);
// this modal just offers to move it into a folder while the founder's
// context is still fresh. Dismissing leaves it at root.
// ---------------------------------------------------------------------------
function SaveToFolderModal({ target, onClose }) {
  const [folderId, setFolderId] = useState("");
  const [busy, setBusy] = useState(false);

  const move = async () => {
    if (!folderId) { onClose(); return; }
    setBusy(true);
    try {
      await api.patch(`/projects/${target.project_id}`, { parent_id: folderId });
      toast.success("Saved to your folder");
      onClose();
    } catch (e) {
      toast.error(formatError(e));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
      data-testid="ai-tool-save-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-primary">
              Saved to Projects
            </p>
            <p className="font-display text-xl mt-1">
              Move "{target.project_name}" into a folder?
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Every generation is auto-saved at the root of your Projects.
              Move it into a launch folder now to keep things tidy.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="ai-tool-save-modal-close"
            className="w-7 h-7 grid place-items-center rounded-full hover:bg-muted"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-5">
          <ProjectFolderPicker
            value={folderId}
            onChange={setFolderId}
            testidPrefix="ai-tool-save-folder"
          />
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="ai-tool-save-modal-skip"
            className="text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            Keep at root
          </button>
          <button
            type="button"
            onClick={move}
            disabled={!folderId || busy}
            data-testid="ai-tool-save-modal-confirm"
            className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FolderOpen className="w-3.5 h-3.5" />
            )}
            Move to folder
          </button>
        </div>
      </div>
    </div>
  );
}
